/** @format */

import chevy from "../assets/logos/chevy.png";
import ford from "../assets/logos/ford.png";
import ram from "../assets/logos/ram.png";
import toyota from "../assets/logos/toyota.png";

import {
  CAUTION_FLAG,
  CHECKERED_FLAG,
  GREEN_FLAG,
  POST_RACE,
  RED_FLAG,
} from "./constants";

// Replaced by `hasFeature(runData, "vehicle_telemetry")` from
// `web/src/utils/hasFeature.ts` — the canonical per-series UI gate.
// `GRCUP_SERIES_ID` and `isGRCupRace` were removed in the supported-features
// rollout; series-id checks should not be re-introduced.

export const capFirst = (str: string) => str?.charAt(0)?.toUpperCase() + str?.slice(1);

export const titleCase = (str: string) => str?.split(" ")?.map(capFirst)?.join(" ");

export function invertColor(hex: string | null, bw: boolean) {
  if (hex === null) {
    return "#ffffff";
  }
  if (hex?.indexOf("#") === 0) {
    hex = hex?.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex?.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex?.length !== 6) {
    return "#ffffff";
  }
  const r = parseInt(hex?.slice(0, 2), 16),
    g = parseInt(hex?.slice(2, 4), 16),
    b = parseInt(hex?.slice(4, 6), 16);
  if (bw) {
    // https://stackoverflow.com/a/3943023/112731
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000000" : "#FFFFFF";
  }
  // invert color components

  // pad each with zeros and return
  return `#${padZero((255 - r).toString(16))}${padZero(
    (255 - g).toString(16)
  )}${padZero((255 - b).toString(16))}`;
}

function padZero(str: string, len = 2) {
  const zeros = new Array(len).join("0");
  return (zeros + str).slice(-len);
}

export const median = (arr: number[]) => {
  const mid = Math.floor(arr.length / 2),
    nums = [...arr].sort((a, b) => a - b);
  return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};

/**
 * @deprecated Use useSeriesLabel() or useSeriesAbbreviation() from @/api/configApi instead.
 * These hooks provide historical series names (e.g., "Winston Cup Series" for 1995)
 * and fall back to these values when config hasn't loaded.
 *
 * This lookup is kept for backward compatibility and iteration over series keys.
 */
export const seriesLookup = {
  1: "NASCAR Cup Series",
  2: "NASCAR Xfinity Series",
  3: "NASCAR Craftsman Truck Series",
  9999: "Toyota GR Cup",
} as Record<string, string>;

export const getFlagIcon = (flagState: number): string | null => {
  switch (flagState) {
    case POST_RACE:
    case CHECKERED_FLAG: //checkerboard
      return "/checker.png";
    case RED_FLAG: //red
      return "/red.png";
    case CAUTION_FLAG: //yellow
      return "/yellow.png";
    case GREEN_FLAG: //green
      return "/green.png";
    default:
      return null;
  }
};

export const getLogo = (manufacturer: string | undefined): string | null => {
  switch (manufacturer?.toLowerCase()) {
    case "ram":
      return ram;
    case "chevrolet":
    case "chv":
      return chevy;
    case "ford":
    case "frd":
      return ford;
    case "toyota":
    case "tyt":
      return toyota;
    default:
      return null;
  }
};

const english_ordinal_rules = new Intl.PluralRules("en", { type: "ordinal" });

const suffixes = {
  one: "st",
  two: "nd",
  few: "rd",
  other: "th",
  zero: "th",
} as Record<string, string>;

export const ordinal = (number: number) =>
  number + suffixes[english_ordinal_rules.select(number)];

const optionsDefault = {
  padEnd: 0,
  padEndChar: "\u2007",
  padStart: 0,
  padStartChar: "\u2007",
  zero: "",
} as DefaultFormatOptions;

export const formatPlus = (
  number: number | null | undefined,
  fixed = 3,
  options: FormatOptions = optionsDefault
) => {
  const { padEnd, padEndChar, padStart, padStartChar, zero } = {
    ...optionsDefault,
    ...options,
  };

  // Handle null, undefined, and NaN explicitly
  if (number === null || number === undefined || Number.isNaN(number)) {
    return zero || "-";
  }

  // Handle zero
  if (number === 0) {
    return zero
      ?.padStart(padStart || 0, padStartChar || "\u2007")
      ?.padEnd(padEnd, padEndChar || "0");
  }

  // Adaptive decimals: show ~4 total digits (before + after decimal)
  const absVal = Math.abs(number);
  const intDigits = absVal >= 1 ? Math.floor(Math.log10(absVal)) + 1 : 1;
  const decimals = Math.max(0, Math.min(fixed, 4 - intDigits));

  return (number > 0 ? `+${number.toFixed(decimals)}` : number.toFixed(decimals))
    ?.padStart(padStart || 0, padStartChar || "\u2007")
    ?.padEnd(padEnd, padEndChar || "0");
};

/**
 * Safely converts a number to a fixed decimal string.
 * Returns fallback string for null, undefined, or NaN values.
 *
 * @param value - The number to format (can be null, undefined, or NaN)
 * @param decimals - Number of decimal places (default: 2)
 * @param fallback - String to return for invalid values (default: "-")
 * @returns Formatted string or fallback
 */
export const safeToFixed = (
  value: number | null | undefined,
  decimals = 2,
  fallback = "-"
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return fallback;
  }
  return value.toFixed(decimals);
};

const arrOfArrsToObj = (arr: [string, unknown][]) =>
  Object.fromEntries(arr) as Record<string, unknown>;

export const searchParamsToObj = (searchParams: URLSearchParams) =>
  arrOfArrsToObj(Array.from(searchParams.entries()));

// Provide a typed optional property for iOS Safari
declare global {
  interface Navigator {
    /** iOS Safari only: true when launched from Home Screen */
    standalone?: boolean;
  }
}

export type PWAInstallSignals = {
  /** Standards-based media query for installed apps */
  displayModeStandalone: boolean;
  /** Some platforms may report fullscreen */
  displayModeFullscreen: boolean;
  /** Some platforms may use minimal-ui when installed */
  displayModeMinimalUi: boolean;
  /** iOS-only WebKit flag when launched from Home Screen */
  navigatorStandalone: boolean;
  /** Android Trusted Web Activity / PWA referrer hint */
  androidTWAReferrer: boolean;
  /** Page is controlled by a registered service worker */
  serviceWorkerControlled: boolean;
};

export type PWAInstallState = {
  installed: boolean;
  /** Best-effort classification of how we're running */
  displayMode: "twa" | "standalone" | "fullscreen" | "minimal-ui" | "browser";
  signals: PWAInstallSignals;
};

export const getPWAInstallState = (): PWAInstallState => {
  // Guard for SSR / non-browser contexts
  const hasWindow = typeof window !== "undefined" && !!window;
  const hasNavigator = typeof navigator !== "undefined" && !!navigator;
  const hasDocument = typeof document !== "undefined" && !!document;

  const mq = (query: string): boolean =>
    hasWindow && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;

  const displayModeStandalone = mq("(display-mode: standalone)");
  const displayModeFullscreen = mq("(display-mode: fullscreen)");
  const displayModeMinimalUi = mq("(display-mode: minimal-ui)");

  const navigatorStandalone =
    hasNavigator && typeof navigator.standalone === "boolean"
      ? !!navigator.standalone
      : false;

  const androidTWAReferrer =
    hasDocument && typeof document.referrer === "string"
      ? document.referrer.startsWith("android-app://")
      : false;

  const serviceWorkerControlled =
    hasNavigator && typeof navigator.serviceWorker !== "undefined"
      ? !!navigator.serviceWorker?.controller
      : false;

  const installed = Boolean(
    displayModeStandalone ||
      displayModeFullscreen ||
      displayModeMinimalUi ||
      navigatorStandalone ||
      androidTWAReferrer
  );

  // Precedence for labeling
  const displayMode: PWAInstallState["displayMode"] = androidTWAReferrer
    ? "twa"
    : displayModeStandalone
      ? "standalone"
      : displayModeFullscreen
        ? "fullscreen"
        : displayModeMinimalUi
          ? "minimal-ui"
          : "browser";

  return {
    installed,
    displayMode,
    signals: {
      displayModeStandalone,
      displayModeFullscreen,
      displayModeMinimalUi,
      navigatorStandalone,
      androidTWAReferrer,
      serviceWorkerControlled,
    },
  };
};

export const isInStandaloneMode = (): boolean => getPWAInstallState().installed;

// ── PWA Platform Detection ──────────────────────────────────────────────

export type PWAPlatform =
  | { type: "chromium"; canPrompt: true }
  | { type: "ios"; canPrompt: false }
  | { type: "macos-safari"; canPrompt: false }
  | { type: "unsupported"; canPrompt: false };

export const getPWAPlatform = (): PWAPlatform => {
  if (typeof navigator === "undefined") {
    return { type: "unsupported", canPrompt: false };
  }

  const ua = navigator.userAgent;

  // iOS / iPadOS — navigator.standalone exists only on iOS WebKit
  const isIOS =
    /iPhone|iPad|iPod/.test(ua) || typeof navigator.standalone === "boolean";
  if (isIOS) {
    return { type: "ios", canPrompt: false };
  }

  // Chromium-based browsers support BeforeInstallPromptEvent
  if ("BeforeInstallPromptEvent" in window) {
    return { type: "chromium", canPrompt: true };
  }

  // macOS Safari (not Chromium): UA has "Safari" but not "Chrome"/"Chromium"
  const isMac = /Macintosh/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS/.test(ua);
  if (isMac && isSafari) {
    return { type: "macos-safari", canPrompt: false };
  }

  return { type: "unsupported", canPrompt: false };
};
